/* eslint-disable ember/classic-decorator-no-classic-methods, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { action } from '@ember/object';

export default class GroupsController extends Controller {
  @service
  session;

  @service
  store;

  @service
  toaster;

  @tracked
  groupName = '';

  @action
  setGroupName(value) {
    this.groupName = value;
  }

  @tracked
  groupDescription = '';

  @action
  setGroupDescription(value) {
    this.groupDescription = value;
  }

  @tracked
  groupKey = '';

  @action
  setGroupKey(value) {
    this.groupKey = value;
  }

  @tracked
  groupIcon = '';

  @action
  setGroupIcon(value) {
    this.groupIcon = value;
  }

  @tracked
  groupSequence = 0;

  @action
  setGroupSequence(value) {
    this.groupSequence = value;
  }

  @tracked
  heroOnly = false;

  @action
  setHeroOnly(value) {
    this.heroOnly = value;
  }

  @tracked
  groupId = '';

  @tracked
  groupDialogOpen = false;

  @tracked
  saveInProgress = false;

  @tracked
  deleteGroupDialogOpen = false;

  @tracked
  deleteInProgress = false;

  @tracked
  errorMessage = '';

  @tracked
  isSortable = false;

  // toggleDialog opens the group dialog.
  toggleDialog() {
    this.errorMessage = '';
    this.toggleProperty('groupDialogOpen');
    if (!this.groupDialogOpen) {
      this.resetForm();
    }
  }

  @action
  toggleGroupDialog() {
    this.toggleDialog();
  }

  @action
  editGroup(id) {
    this.store.findRecord('school-options/group', id).then((group) => {
      this.groupName = group.get('name');
      this.groupDescription = group.get('description');
      this.groupKey = group.get('key');
      this.groupIcon = group.get('icon');
      this.groupSequence = group.get('sequence');
      this.heroOnly = group.get('heroOnly');
    });
    this.groupId = id;
    this.toggleDialog();
  }

  @action
  saveGroup() {
    this.saveInProgress = true;
    this.errorMessage = '';
    let promise;
    let action = 'create';

    if (isEmpty(this.groupId)) {
      promise = new Promise((resolve) => {
        let record = this.store.createRecord('school-options/group', {
          name: this.groupName,
          description: this.groupDescription,
          key: this.groupKey,
          icon: this.groupIcon,
          sequence: this.groupSequence,
          heroOnly: this.heroOnly,
        });

        resolve(record);
      });
    } else {
      action = 'update';
      promise = new Promise((resolve) => {
        const id = this.groupId;
        this.store.findRecord('school-options/group', id).then((group) => {
          group.set('name', this.groupName);
          group.set('description', this.groupDescription);
          group.set('key', this.groupKey);
          group.set('icon', this.groupIcon);
          group.set('sequence', this.groupSequence);
          group.set('heroOnly', this.heroOnly);

          resolve(group);
        });
      });
    }

    promise.then((record) => {
      return record
        .save()
        .then(() => {
          this.groupDialogOpen = false;
          this.resetForm();
        })
        .catch((err) => {
          this.handleError(err, action);
          record.rollbackAttributes();
        })
        .then(() => {
          this.saveInProgress = false;
        });
    });
  }

  @action
  deleteGroup(id) {
    this.errorMessage = '';
    this.store.findRecord('school-options/group', id).then((group) => {
      this.groupName = group.get('name');
    });
    this.groupId = id;
    this.deleteGroupDialogOpen = true;
  }

  @action
  confirmDelete(confirmed) {
    return new Promise((resolve, reject) => {
      if (confirmed) {
        // Deletion confirmed; actually delete the record.
        this.deleteInProgress = true;

        // Grab record from local store - no GET request, as that causes weirdness.
        // We only need the ID to issue the DELETE anyway, which can't change, so no need to use findRecord.
        let group = this.store.peekRecord('school-options/group', this.groupId);

        // Delete record (locally + pushed to server).
        return group
          .destroyRecord()
          .then(resolve)
          .catch((err) => {
            // Remove 'deleted' flag so that entity can be interacted with again.
            group.rollbackAttributes();
            reject(err);
          });
      } else {
        resolve();
      }
    })
      .then(() => {
        // Tidy up / close dialog.
        this.deleteGroupDialogOpen = false;
        this.groupId = '';
        this.groupName = '';
      })
      .catch((err) => {
        this.handleError(err, 'delete');
      })
      .then(() => {
        this.deleteInProgress = false;
      });
  }

  // drag-drop sorting
  @action
  sortDragEnd({ sourceList, sourceIndex, targetList, targetIndex }) {
    if (sourceList === targetList && sourceIndex === targetIndex) {
      return;
    }

    const item = sourceList[sourceIndex];

    sourceList.splice(sourceIndex, 1);
    targetList.splice(targetIndex, 0, item);

    targetList.forEach((entity, index) => {
      entity.set('sequence', index + 1);
    });
  }

  @action
  startSorting() {
    this.isSortable = true;
  }

  @action
  finishSorting() {
    this.isSortable = false;
    let promises = this.model.groups.reduce((promises, group) => {
      if (group.hasDirtyAttributes) {
        promises.push(group.save());
      }
      return promises;
    }, []);

    Promise.all(promises)
      .then(() => {
        this.toaster.success('Sequence saved');
      })
      .catch(() => {
        this.toaster.error('Error saving sequence.  Please try again.');
        this.model.groups.forEach((group) => group.rollbackAttributes());
      });
  }

  handleError(err) {
    if (!isEmpty(err.errors.msg)) {
      this.errorMessage = err.errors.msg;
    } else {
      this.errorMessage = err.message;
    }
  }

  setErrorMessage(code, action) {
    var message = 'An unknown error occurred.  Please try again.';
    const codes = {
      400: 'Sorry, your request was invalid.  Please check all details below, and try again.',
      403: {
        create:
          'Sorry, you are not authorised to create a new user.  Please check that you are logged in as the correct user, and try again.',
        update:
          'Sorry, you are not authorised to edit this user.  Please check that you are logged in as the correct user, and try again.',
        delete:
          'Sorry, you are not authorised to delete this user.  Please check that you are logged in as the correct user, and try again.',
      },
      409: 'A user already exists with that username.  Please double-check the details, or find and update the existing user.',
    };

    if (!isEmpty(codes[code][action])) {
      message = codes[code][action];
    } else if (!isEmpty(codes[code])) {
      message = codes[code];
    }

    this.errorMessage = message;
  }

  resetForm() {
    this.groupId = '';
    this.groupName = '';
    this.groupDescription = '';
    this.groupKey = '';
    this.groupIcon = '';
    this.groupSequence = '';
    this.heroOnly = false;
  }
}
